import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';

import { PageLoading } from 'modules/common/layout';
import { Page as Url, Redirect } from 'modules/common/routing';
import {
  Store as ApplicationStore,
  Status as ApplicationStatus,
} from 'modules/application/core';
import { Store as StepsStore } from 'modules/application/steps';
import { Store as LoanStore, Status as LoanStatus } from 'modules/loan';
import { Store as ClientStore, Helper as ClientHelper } from 'modules/client';

/**
 * Посадочная страница личного кабинета. Определяет, на каком этапе user flow
 * находится текущий пользователь и совершает перенаправление на
 * соответствующую страницу.
 */
const Page: FC = () => {
  const application = ApplicationStore.use();
  const loan = LoanStore.use();
  const steps = StepsStore.use();
  const client = ClientStore.use();

  useEffect(() => {
    application.touch();
    loan.touch();
    client.touch();
  }, [application, loan, client]);

  if (application.status == null || loan.status == null || !client.ready) {
    return <PageLoading />;
  }

  switch (application.status) {
    case ApplicationStatus.FILLING: {
      steps.navigateToCurrentStep();
      return <PageLoading />;
    }

    case ApplicationStatus.PENDING: {
      return (
        <Redirect url={Url.UNDERWRITING} replace>
          <PageLoading />
        </Redirect>
      );
    }

    case ApplicationStatus.REJECTED: {
      return (
        <Redirect url={Url.REFUSAL} replace>
          <PageLoading />
        </Redirect>
      );
    }

    default: {
      break;
    }
  }

  switch (loan.status) {
    case LoanStatus.SIGNING: {
      return (
        <Redirect url={Url.CONTRACT} replace>
          <PageLoading />
        </Redirect>
      );
    }

    case LoanStatus.SIGNED: {
      return (
        <Redirect url={Url.PAYOUT} replace>
          <PageLoading />
        </Redirect>
      );
    }

    case LoanStatus.PAYOUT: {
      return (
        <Redirect url={Url.PAYOUT_SUCCESS} replace>
          <PageLoading />
        </Redirect>
      );
    }

    case LoanStatus.ACTIVE: {
      return (
        <Redirect url={Url.LOAN} replace>
          <PageLoading />
        </Redirect>
      );
    }

    case LoanStatus.CLOSED: {
      return (
        <Redirect url={Url.HOME} replace>
          <PageLoading />
        </Redirect>
      );
    }

    default: {
      break;
    }
  }

  if (
    application.status === ApplicationStatus.NONE &&
    loan.status === LoanStatus.NONE
  ) {
    const isFull = ClientHelper.isFull(client.model);

    if (isFull) {
      return <Redirect url={Url.HOME} replace />;
    }

    return <Redirect url={Url.APPLICATION_FIRST_STEP} replace />;
  }

  return <PageLoading />;
};

const component = observer(Page);
export { component as Page };
